<template>
  <RevCardCarousel
    ref="carousel"
    class="[&_li]:rounded-lg [&_li]:w-[223px] [&_li]:bg-[url('/img/product/verified-refurbished/fingerprint.svg'),_url('/img/product/verified-refurbished/holo.avif')] [&_li]:bg-blend-overlay [&_li]:[background-attachment:scroll,fixed] [&_li]:supports-[(-webkit-touch-callout:none)]:[background-attachment:scroll]"
    :next-alternative-text="i18n(translations.nextSlideText)"
    :prev-alternative-text="i18n(translations.prevSlideText)"
    :subtitle="checkpoints.subtitle"
    :title="checkpoints.title"
  >
    <Card
      v-for="(checkpoint, index) in checkpoints.items"
      :key="checkpoint.text"
      :checkpoint
      :index
      @click="onCardClick(index)"
    />
  </RevCardCarousel>
  <ClientOnly>
    <VerifiedRefurbishedCarouselModal
      :checkpoints
      :opened-index="effectiveOpenedIndex"
      @close="cardClickIndex = null"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCardCarousel } from '@ds/components/CardCarousel'

import type { GetVerifiedRefurbishCarouselResponse } from '~/api/product-page/types'
import { useScrollPinnedBackground } from '~/scopes/product-core/composables/useScrollPinnedBackground/useScrollPinnedBackground'

import translations from './VerifiedRefurbishedCarousel.translations'
import Card from './components/Card/Card.vue'
import VerifiedRefurbishedCarouselModal from './components/VerifiedRefurbishedCarouselModal/VerifiedRefurbishedCarouselModal.vue'

const props = withDefaults(
  defineProps<{
    categoryId: string
    checkpoints: GetVerifiedRefurbishCarouselResponse
    openedIndex?: number
  }>(),
  {
    openedIndex: 0,
  },
)

const i18n = useI18n()

const cardClickIndex = ref<number | null>(null)

const effectiveOpenedIndex = computed(() =>
  cardClickIndex.value !== null ? cardClickIndex.value : props.openedIndex,
)

function onCardClick(index: number) {
  cardClickIndex.value = index
}

// iOS Safari does not support `background-attachment: fixed`, so the CSS above
// falls back to `scroll` on iOS and the holo parallax is lost. We re-create it
// here by pinning the holo layer to the viewport in JS: for each card we offset
// its `background-position` by the card's position relative to the viewport,
// which mimics what `fixed` does natively. The fingerprint layer (first one)
// keeps its default `left top` so it scrolls with the card as before.
const carouselRef = useTemplateRef<{ $el: HTMLElement }>('carousel')

const isIos =
  typeof CSS !== 'undefined' && CSS.supports('-webkit-touch-callout: none')

let cards: HTMLElement[] = []

function paintParallax() {
  if (!cards.length) {
    cards = Array.from(carouselRef.value?.$el?.querySelectorAll('li') ?? [])
  }
  for (const card of cards) {
    const rect = card.getBoundingClientRect()
    card.style.backgroundPosition = `left top, ${-rect.left}px ${-rect.top}px`
  }
}

useScrollPinnedBackground(isIos, paintParallax)
</script>
